import axios, { AxiosResponse } from 'axios';
import { ApiResponse, ApiMessage } from '../types/interfaces';
import { showToast } from './toastService';
import { API_BASE_URL } from './apiConfig';

let setLoading: (loading: boolean) => void;
let refreshTokenPromise: Promise<string> | null = null;

export const initializeLoadingState = (setLoadingFunction: (loading: boolean) => void) => {
  setLoading = setLoadingFunction;
};

const api = axios.create({
  baseURL: API_BASE_URL,
});

const handleLogout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  window.location.href = '/';
};

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
  const newAccessToken = response.data.accessToken;
  const newRefreshToken = response.data.refreshToken;
  
  localStorage.setItem('token', newAccessToken);
  localStorage.setItem('refreshToken', newRefreshToken);
  
  return newAccessToken;
};

api.interceptors.request.use(
  async (config) => {
    setLoading(true);
    const token = localStorage.getItem('token') ?? '';

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    setLoading(false);
    showToast({ level: 'error', text: 'Request error' });
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<any>>) => {
    setLoading(false);
    const responseType = response.config.responseType;
    const contentType = response.headers['content-type'];

    if (
      responseType === 'blob'
      || responseType === 'arraybuffer'
      || (typeof contentType === 'string' && contentType.startsWith('audio/'))
    ) {
      return response;
    } else if (response.data && response.data.data !== undefined && Array.isArray(response.data.messages)) {
      const apiResponse: ApiResponse<any> = response.data;
      apiResponse.messages.forEach((message: ApiMessage) => showToast(message));
      return response;
    } else if (response.status === 204) {
      return response;
    } else {
      console.error('Response does not follow the expected schema', response);
      showToast({ level: 'error', text: 'Unexpected response schema' });
      return Promise.reject(new Error('Response does not follow the expected schema'));
    }
  },
  async (error) => {
    setLoading(false);
    console.error('API response error:', error);
    const originalRequest = error.config;
    
    if (error.response) {
      if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        
        try {
          // Prevent multiple simultaneous refresh requests
          if (!refreshTokenPromise) {
            refreshTokenPromise = refreshAccessToken().finally(() => {
              refreshTokenPromise = null;
            });
          }
          
          const newAccessToken = await refreshTokenPromise;
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          console.error('Failed to refresh token', refreshError);
          showToast({ level: 'error', text: 'Istuntosi on vanhentunut. Kirjaudu uudelleen sisään.' });
          handleLogout();
          return Promise.reject(refreshError);
        }
      } else if (error.response.status >= 500) {
        console.error('Server error:', error.response);
        showToast({ level: 'error', text: 'An unexpected error occurred. Please try again later.' });
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
      showToast({ level: 'error', text: 'No response from server. Please check your network connection.' });
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error in request setup:', error.message);
      showToast({ level: 'error', text: 'An unexpected error occurred. Please try again later.' });
    }
    return Promise.reject(error);
  }
);

export default api;
